/* ============================================================================
   DN CUSTOM START - Expected Results Slider
   Isolated custom element. It does not register or modify the theme's original
   before/after slider JavaScript.
   ========================================================================== */

(() => {
  'use strict';

  const TAG_NAME = 'dn-results-comparison';

  if (customElements.get(TAG_NAME)) return;

  class DNResultsComparison extends HTMLElement {
    constructor() {
      super();

      this.viewport = null;
      this.handle = null;
      this.labels = [];
      this.contents = [];
      this.hints = [];

      this.steps = 1;
      this.percent = 0;
      this.activeIndex = 0;
      this.dragging = false;
      this.hasDragged = false;
      this.animationTimer = null;
      this.introObserver = null;
      this.introPlayed = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onLabelClick = this.onLabelClick.bind(this);
    }

    connectedCallback() {
      if (this.dataset.dnReady === 'true') return;

      this.viewport = this.querySelector('[data-dn-rc-viewport]');
      this.handle = this.querySelector('[data-dn-rc-handle]');
      this.labels = Array.from(this.querySelectorAll('[data-dn-rc-label]'));
      this.contents = Array.from(this.querySelectorAll('[data-dn-rc-content]'));
      this.hints = Array.from(this.querySelectorAll('[data-dn-rc-hint]'));
      this.steps = Math.max(1, Number.parseInt(this.dataset.steps || this.labels.length || '1', 10));

      if (!this.viewport || !this.handle) return;

      this.dataset.dnReady = 'true';

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.handle.addEventListener('keydown', this.onKeyDown);
      this.labels.forEach((label) => label.addEventListener('click', this.onLabelClick));

      const start = Number.parseFloat(this.dataset.start || '25');
      this.setPercent(Number.isFinite(start) ? start : this.targetForIndex(0), {
        animate: false,
        force: true
      });

      this.setupIntro();
    }

    disconnectedCallback() {
      if (!this.viewport || !this.handle) return;

      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.handle.removeEventListener('keydown', this.onKeyDown);
      this.labels.forEach((label) => label.removeEventListener('click', this.onLabelClick));

      if (this.animationTimer) window.clearTimeout(this.animationTimer);
      if (this.introObserver) this.introObserver.disconnect();
      delete this.dataset.dnReady;
    }

    get shouldSnap() {
      return this.dataset.snap === 'true';
    }

    get labelsClickable() {
      return this.dataset.clickable !== 'false';
    }

    get shouldHideHint() {
      return this.dataset.hideHint !== 'false';
    }

    clamp(value, min = 5, max = 95) {
      return Math.min(max, Math.max(min, value));
    }

    targetForIndex(index) {
      const safeIndex = Math.min(this.steps - 1, Math.max(0, index));
      return this.clamp(((safeIndex + 1) / this.steps) * 100);
    }

    indexFromPercent(percent) {
      const rawIndex = Math.ceil((this.clamp(percent) / 100) * this.steps) - 1;
      return Math.min(this.steps - 1, Math.max(0, rawIndex));
    }

    percentFromClientX(clientX) {
      const rect = this.viewport.getBoundingClientRect();
      if (!rect.width) return this.percent;
      return this.clamp(((clientX - rect.left) / rect.width) * 100);
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest('[data-dn-rc-label]')) return;

      event.preventDefault();
      this.dragging = true;
      this.classList.add('is-dragging');
      this.classList.remove('is-animating');

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Pointer capture is an enhancement; dragging still works without it.
      }

      this.setPercent(this.percentFromClientX(event.clientX), { animate: false });
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      event.preventDefault();
      this.setPercent(this.percentFromClientX(event.clientX), { animate: false });
    }

    onPointerUp(event) {
      if (!this.dragging) return;

      this.dragging = false;
      this.classList.remove('is-dragging');
      this.markDragged();

      try {
        if (this.viewport.hasPointerCapture(event.pointerId)) {
          this.viewport.releasePointerCapture(event.pointerId);
        }
      } catch (_error) {
        // Safe fallback for browsers that do not expose pointer capture state.
      }

      if (this.shouldSnap) this.snapToNearestStep();
    }

    onKeyDown(event) {
      let next = this.percent;
      const smallStep = event.shiftKey ? 5 : 1;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next -= smallStep;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next += smallStep;
          break;
        case 'PageDown':
          next = this.targetForIndex(this.activeIndex - 1);
          break;
        case 'PageUp':
          next = this.targetForIndex(this.activeIndex + 1);
          break;
        case 'Home':
          next = this.targetForIndex(0);
          break;
        case 'End':
          next = this.targetForIndex(this.steps - 1);
          break;
        default:
          return;
      }

      event.preventDefault();
      this.markDragged();
      this.setPercent(next, { animate: true });
    }

    onLabelClick(event) {
      if (!this.labelsClickable) return;

      event.preventDefault();
      event.stopPropagation();

      const index = Number.parseInt(event.currentTarget.dataset.index || '0', 10);
      this.markDragged();
      this.setPercent(this.targetForIndex(index), { animate: true });
      this.handle.focus({ preventScroll: true });
    }

    snapToNearestStep() {
      let nearest = this.targetForIndex(0);
      let distance = Math.abs(this.percent - nearest);

      for (let index = 1; index < this.steps; index += 1) {
        const target = this.targetForIndex(index);
        const currentDistance = Math.abs(this.percent - target);
        if (currentDistance < distance) {
          nearest = target;
          distance = currentDistance;
        }
      }

      this.setPercent(nearest, { animate: true });
    }

    markDragged() {
      if (this.hasDragged) return;
      this.hasDragged = true;
      if (this.shouldHideHint) this.classList.add('has-dragged');
    }

    setPercent(value, { animate = false, force = false } = {}) {
      const next = this.clamp(Number(value));
      if (!force && Math.abs(next - this.percent) < 0.001) return;

      this.percent = next;

      if (animate) {
        this.classList.add('is-animating');
        if (this.animationTimer) window.clearTimeout(this.animationTimer);
        const duration = this.getAnimationDuration();
        this.animationTimer = window.setTimeout(() => {
          this.classList.remove('is-animating');
        }, duration + 80);
      } else {
        this.classList.remove('is-animating');
      }

      this.style.setProperty('--dn-rc-percent', `${next}%`);
      this.handle.setAttribute('aria-valuenow', String(Math.round(next)));

      const nextIndex = this.indexFromPercent(next);
      if (force || nextIndex !== this.activeIndex) {
        this.activeIndex = nextIndex;
        this.updateActiveState();
      }
    }

    updateActiveState() {
      this.labels.forEach((label, index) => {
        const active = index === this.activeIndex;
        label.classList.toggle('is-active', active);
        if (active) {
          label.setAttribute('aria-current', 'step');
        } else {
          label.removeAttribute('aria-current');
        }
      });

      this.contents.forEach((content, index) => {
        const active = index === this.activeIndex;
        content.classList.toggle('is-active', active);
        content.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      const activeLabel = this.labels[this.activeIndex]?.textContent?.trim();
      if (activeLabel) this.handle.setAttribute('aria-valuetext', activeLabel);
    }

    getAnimationDuration() {
      const value = getComputedStyle(this).getPropertyValue('--dn-rc-anim').trim();
      if (!value) return 500;
      if (value.endsWith('ms')) return Number.parseFloat(value) || 500;
      if (value.endsWith('s')) return (Number.parseFloat(value) || 0.5) * 1000;
      return Number.parseFloat(value) || 500;
    }

    setupIntro() {
      if (this.dataset.intro !== 'true' || this.introPlayed) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (!('IntersectionObserver' in window)) {
        this.playIntro();
        return;
      }

      this.introObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (!visible) return;
        this.introObserver.disconnect();
        this.playIntro();
      }, { threshold: 0.35 });

      this.introObserver.observe(this);
    }

    playIntro() {
      if (this.introPlayed || this.hasDragged) return;
      this.introPlayed = true;

      const original = this.percent;
      const segment = 100 / this.steps;
      const nudge = Math.min(5, segment * 0.2);
      const direction = original + nudge < 95 ? 1 : -1;

      window.setTimeout(() => {
        if (this.hasDragged) return;
        this.setPercent(original + (nudge * direction), { animate: true });

        window.setTimeout(() => {
          if (!this.hasDragged) this.setPercent(original, { animate: true });
        }, this.getAnimationDuration() + 160);
      }, 280);
    }
  }

  customElements.define(TAG_NAME, DNResultsComparison);
})();

/* DN CUSTOM END */
